import { NavLink, Link } from "react-router-dom";
import { useState, useEffect, useRef } from "react";
import { Menu, X, Sun, Moon, User as UserIcon, LogOut, BookOpen } from "lucide-react";
import { LOGO_URL } from "@/lib/api";
import { useTheme } from "@/lib/theme";
import { useAuth } from "@/lib/auth";

const links = [
    { to: "/routes", label: "Routes" },
    { to: "/destinations", label: "Destinations" },
    { to: "/journal", label: "Journal" },
    { to: "/about", label: "About" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [menuOpen, setMenuOpen] = useState(false);
    const { theme, toggle } = useTheme();
    const { user, isAuthed, logout } = useAuth();
    const menuRef = useRef(null);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 40);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onClick = (e) => {
            if (menuRef.current && !menuRef.current.contains(e.target)) setMenuOpen(false);
        };
        document.addEventListener("mousedown", onClick);
        return () => document.removeEventListener("mousedown", onClick);
    }, []);

    return (
                <header
            data-testid="site-navbar"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
                scrolled ? "bg-[var(--surface)]/85 backdrop-blur-xl border-b border-[var(--border-hair)]" : "bg-transparent"
            }`}
        >
            <div className="max-w-7xl mx-auto px-6 lg:px-10 h-24 flex items-center justify-between">
                <Link
                    to="/"
                    data-testid="nav-logo-link"
                    className="flex items-center gap-4 group"
                >
                    <img
                        src={LOGO_URL}
                        alt="MotorcycleRoutesIreland.com"
                        className="h-16 w-16 md:h-20 md:w-20 object-contain transition-transform group-hover:scale-105"
                    />
                    <div className="hidden sm:block leading-none">
                        <div className="font-display font-black text-xl md:text-2xl uppercase tracking-tight">
                            Motorcycle<span className="text-[#E87722]">routes</span>
                        </div>
                        <div className="font-display font-bold text-xs md:text-sm uppercase tracking-[0.28em] text-[var(--fg-muted)] mt-1">
                            Ireland
                        </div>
                    </div>
                </Link>

                <nav className="hidden md:flex items-center gap-1">
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            data-testid={`nav-${l.label.toLowerCase()}-link`}
                            className={({ isActive }) =>
                                `px-4 py-2 font-display font-bold uppercase tracking-[0.15em] text-sm transition-colors ${
                                    isActive
                                        ? "text-[#E87722]"
                                        : "text-[var(--fg-muted)] hover:text-[var(--fg)]"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                    <NavLink
                        to="/routes"
                        data-testid="nav-explore-cta"
                        className="ml-3 px-5 py-2.5 bg-[#E87722] text-white font-display font-bold uppercase tracking-[0.15em] text-sm hover:bg-[#F28C38] transition-colors"
                    >
                        Explore Routes
                    </NavLink>
                    <button
                        data-testid="theme-toggle"
                        onClick={toggle}
                        aria-label={theme === "dark" ? "Switch to light mode" : "Switch to dark mode"}
                        className="ml-2 w-11 h-11 flex items-center justify-center border border-[var(--border-hair)] hover:border-[#E87722] text-[var(--fg-muted)] hover:text-[#E87722] transition-colors"
                    >
                        {theme === "dark" ? <Sun size={16} strokeWidth={2} /> : <Moon size={16} strokeWidth={2} />}
                    </button>
                    {/* Auth button */}
                    {isAuthed ? (
                        <div className="relative ml-2" ref={menuRef}>
                            <button
                                data-testid="nav-account-btn"
                                onClick={() => setMenuOpen((v) => !v)}
                                className="w-11 h-11 flex items-center justify-center border border-[var(--border-hair)] hover:border-[#E87722] transition-colors overflow-hidden"
                            >
                                {user?.picture ? (
                                    <img src={user.picture} alt="" className="w-full h-full object-cover" referrerPolicy="no-referrer" />
                                ) : (
                                    <span className="font-display font-black text-[#E87722] text-sm uppercase">
                                        {(user?.name || user?.email || "?").slice(0, 1)}
                                    </span>
                                )}
                            </button>
                            {menuOpen && (
                                <div className="absolute right-0 top-full mt-2 min-w-56 bg-[var(--surface)] border border-[var(--border-hair)] shadow-xl" data-testid="nav-account-menu">
                                    <div className="px-4 py-3 border-b border-[var(--border-hair)]">
                                        <div className="font-display font-bold uppercase text-[var(--fg)] text-sm truncate">{user?.name || "Rider"}</div>
                                        <div className="text-xs text-[var(--fg-dim)] font-body truncate">{user?.email}</div>
                                    </div>
                                    <Link
                                        to="/passport"
                                        onClick={() => setMenuOpen(false)}
                                        data-testid="nav-passport-link"
                                        className="flex items-center gap-3 px-4 py-3 hover:bg-[var(--card-bg)] text-[var(--fg)] font-display font-bold uppercase tracking-[0.18em] text-xs"
                                    >
                                        <BookOpen size={14} /> Rider Passport
                                    </Link>
                                    <button
                                        onClick={() => { setMenuOpen(false); logout(); }}
                                        data-testid="nav-logout-btn"
                                        className="w-full text-left flex items-center gap-3 px-4 py-3 hover:bg-[var(--card-bg)] text-[var(--fg-muted)] hover:text-[#E87722] font-display font-bold uppercase tracking-[0.18em] text-xs"
                                    >
                                        <LogOut size={14} /> Sign out
                                    </button>
                                </div>
                            )}
                        </div>
                    ) : (
                        <NavLink
                            to="/signin"
                            data-testid="nav-signin-link"
                            className="ml-2 h-11 flex items-center justify-center gap-2 px-4 border border-[var(--border-hair)] hover:border-[#E87722] text-[var(--fg-muted)] hover:text-[#E87722] font-display font-bold uppercase tracking-[0.15em] text-xs transition-colors"
                        >
                            <UserIcon size={14} /> Sign in
                        </NavLink>
                    )}
                </nav>

                <div className="flex items-center gap-2 md:hidden">
                    <button
                        data-testid="theme-toggle-mobile"
                        onClick={toggle}
                        aria-label="Toggle theme"
                        className="p-2 text-[var(--fg)]"
                    >
                        {theme === "dark" ? <Sun size={20} /> : <Moon size={20} />}
                    </button>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen(!open)}
                        className="p-2 text-[var(--fg)]"
                        aria-label="Toggle menu"
                    >
                        {open ? <X size={24} /> : <Menu size={24} />}
                    </button>
                </div>
            </div>

            {open && (
                <div className="md:hidden border-t border-[var(--border-hair)] bg-[var(--surface)]/95 backdrop-blur-xl">
                    <div className="px-6 py-6 flex flex-col gap-1">
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                data-testid={`nav-mobile-${l.label.toLowerCase()}-link`}
                                onClick={() => setOpen(false)}
                                className={({ isActive }) =>
                                    `py-3 font-display font-bold uppercase tracking-[0.2em] text-lg ${
                                        isActive ? "text-[#E87722]" : "text-[var(--fg)]"
                                    }`
                                }
                            >
                                {l.label}
                            </NavLink>
                        ))}
                        {isAuthed ? (
                            <>
                                <NavLink
                                    to="/passport"
                                    onClick={() => setOpen(false)}
                                    data-testid="nav-mobile-passport-link"
                                    className={({ isActive }) => `py-3 font-display font-bold uppercase tracking-[0.2em] text-lg ${isActive ? "text-[#E87722]" : "text-[var(--fg)]"}`}
                                >
                                    Passport
                                </NavLink>
                                <button onClick={() => { setOpen(false); logout(); }} data-testid="nav-mobile-logout-btn" className="text-left py-3 font-display font-bold uppercase tracking-[0.2em] text-lg text-[var(--fg-muted)]">
                                    Sign out
                                </button>
                            </>
                        ) : (
                            <NavLink
                                to="/signin"
                                onClick={() => setOpen(false)}
                                data-testid="nav-mobile-signin-link"
                                className={({ isActive }) => `py-3 font-display font-bold uppercase tracking-[0.2em] text-lg ${isActive ? "text-[#E87722]" : "text-[var(--fg)]"}`}
                            >
                                Sign in
                            </NavLink>
                        )}
                    </div>
                </div>
            )}
        </header>
    );
};
